import { useCallback, useEffect, useState } from "react";
import { BASE_API_URL } from "../../Components/config";

const API = `${BASE_API_URL}/api/v1/course-segments`;

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
});

/**
 * A classroom's sessions. The list endpoint carries each session's phase
 * counts, so opening a session needs no extra request to show them.
 */
export function useSessions(courseGuid) {
  const [sessions, setSessions] = useState(null); // null = still loading
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!courseGuid) return;
    try {
      const res = await fetch(`${API}/sessions/${courseGuid}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`load failed (${res.status})`);
      const data = await res.json();
      setSessions(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      console.error(e);
      setSessions([]);
      setError("Couldn't load the sessions for this space.");
    }
  }, [courseGuid]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = async ({ name, scheduledAt, durationMinutes, questionsEnabled = true }) => {
    const res = await fetch(`${API}/`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({
        course_guid: courseGuid,
        name,
        // Sessions are addressed by guid; the code is only a human-readable
        // handle, so it is derived rather than asked for.
        code: name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "-").slice(0, 32) || "SESSION",
        kind: "SESSION",
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        duration_minutes: durationMinutes || null,
        questions_enabled: questionsEnabled,
      }),
    });
    if (!res.ok) throw new Error("create failed");
    const created = await res.json();
    await refresh();
    return created;
  };

  const update = async (guid, { name, scheduledAt, durationMinutes, questionsEnabled }) => {
    const body = {};
    if (name !== undefined) body.name = name;
    if (durationMinutes !== undefined) body.duration_minutes = durationMinutes || null;
    if (questionsEnabled !== undefined) body.questions_enabled = questionsEnabled;
    if (scheduledAt !== undefined) {
      body.scheduled_at = scheduledAt ? new Date(scheduledAt).toISOString() : null;
    }
    const res = await fetch(`${API}/${guid}`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error("update failed");
    await refresh();
  };

  const remove = async (guid) => {
    const res = await fetch(`${API}/${guid}`, { method: "DELETE", headers: authHeaders() });
    if (!res.ok) throw new Error("delete failed");
    await refresh();
  };

  return { sessions, error, refresh, create, update, remove };
}
